import type { CommentRecord } from '@packages/clients';

import type { TechlineCommentEntity } from '../entities';

import { TechlineStatusCode } from '../../cases/types';

/**
 * Map a techline case status code to the API string format used in comment
 * and status update requests. OPEN_ESCALATED maps to 'OPEN-ESCALATED';
 * all other statuses map to 'PENDING TECHLINE'.
 *
 * @param statusCode - the current case status code
 * @returns the API-formatted status string
 * @example
 * ```ts
 * mapStatusCodeToApiString(TechlineStatusCode.ESCALATED); // 'ESCALATED'
 * mapStatusCodeToApiString(TechlineStatusCode.IN_REVIEW);  // 'PENDING TECHLINE'
 * ```
 */
export function mapStatusCodeToApiString(statusCode: TechlineStatusCode): string {
    if (statusCode === TechlineStatusCode.ESCALATED) {
        return 'ESCALATED';
    }
    return 'PENDING TECHLINE';
}

/**
 * Map a {@link CommentRecord} from the client layer to a {@link TechlineCommentEntity}. Currently a 1:1
 * mapping since the record and entity share the same shape, but this mapper exists to decouple the domain
 * layer from the client layer and to provide a stable insertion point if the shapes diverge in the future.
 *
 * Exception: 1:1 mappers normally should not exist per domain conventions, but this one is kept intentionally
 * to maintain the DTO-to-entity boundary firewall. Removing it would couple operations directly to the DTO
 * shape, which would silently break if the backend response diverges from the entity in the future.
 *
 * @param dto - the comment record returned by the backend API
 * @returns a domain-level comment entity
 * @example
 * ```ts
 * const entity = toTechlineCommentEntity({
 *     createdOn: '2024-06-15T10:30:00Z',
 *     fullName: 'John Doe',
 *     jobTitle: 'Service Technician',
 *     message: 'Replaced the sensor.',
 *     retailerNumber: 'R001',
 *     userName: 'jdoe',
 * });
 * ```
 */
export function toTechlineCommentEntity(dto: CommentRecord): TechlineCommentEntity {
    return {
        createdOn: dto.createdOn,
        fullName: dto.fullName,
        jobTitle: dto.jobTitle,
        message: dto.message,
        retailerNumber: dto.retailerNumber,
        userName: dto.userName,
    };
}
